import { useEffect, useMemo, useRef, useState } from "react";
import "./index.css";

import { FONT_OPTIONS, type DesignConfig, type FontKind } from "./design";
import { applyValues, buildPreviewHtml } from "./previewTemplate";
import { TEMPLATES, TEMPLATE_LIST, makeDefaultConfig } from "./templates";
import {
  mergeDefaults,
  type Field,
  type FieldGroup,
} from "./templates/shared";
import { buildPrompt, type PromptTarget } from "./exportPrompt";
import { copyText, downloadText, toYaml } from "./serialize";

// フォームに出すグループの順序。テンプレートの fields をこの順に並べて描く。
const GROUP_ORDER: FieldGroup[] = [
  "カラー",
  "タイポグラフィ",
  "コンテンツ",
  "画像",
];

export function App() {
  const [config, setConfig] = useState<DesignConfig>(makeDefaultConfig);
  const [showOutput, setShowOutput] = useState(false);

  const def = TEMPLATES[config.template];

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [previewReady, setPreviewReady] = useState(false);

  // 骨格HTMLはテンプレート切替時のみ作り直す(=色/フォント/文言/画像の変更では
  // iframe を再ロードしない)。作り直す瞬間の最新 config を焼き込むため、ref 経由で
  // 参照し、依存配列は template だけにする。
  const latest = useRef(config);
  latest.current = config;
  const skeleton = useMemo(
    () => buildPreviewHtml(latest.current, { editing: true }),
    [config.template],
  );

  // 値の変更は再ロードせず、ロード済みドキュメントへ直接反映する。
  useEffect(() => {
    if (!previewReady) return;
    const doc = iframeRef.current?.contentDocument;
    if (doc) applyValues(doc, def, config.values);
  }, [config, def, previewReady]);

  // 画像スロットのクリックでファイル選択を開く。iframe は sandbox でスクリプト
  // 不可のため、親から contentDocument にハンドラを付ける(applyValues と同様)。
  const pendingSlot = useRef<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const openPicker = (key: string) => {
    pendingSlot.current = key;
    fileInputRef.current?.click();
  };

  useEffect(() => {
    if (!previewReady) return;
    const doc = iframeRef.current?.contentDocument;
    if (!doc) return;
    const onClick = (e: Event) => {
      const key = (e.currentTarget as HTMLElement).dataset.img;
      if (key) openPicker(key);
    };
    const slots = Array.from(doc.querySelectorAll<HTMLElement>("[data-img]"));
    slots.forEach((el) => el.addEventListener("click", onClick));
    return () => slots.forEach((el) => el.removeEventListener("click", onClick));
  }, [previewReady]);

  const onPickFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.currentTarget;
    const file = input.files?.[0];
    const key = pendingSlot.current;
    input.value = ""; // 同じファイルを選び直せるようリセット
    pendingSlot.current = null;
    if (!file || !key) return;
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === "string") setValue(key, reader.result);
    };
    reader.readAsDataURL(file);
  };

  const setValue = (key: string, value: string) =>
    setConfig((prev) => ({ ...prev, values: { ...prev.values, [key]: value } }));

  const setTemplate = (template: DesignConfig["template"]) => {
    // テンプレート切替は srcDoc を作り直して iframe を再ロードさせる。
    // onLoad まで previewReady を落とし、旧ドキュメントへ反映しないようにする。
    setPreviewReady(false);
    setConfig((prev) => ({
      template,
      // 重なるキー(色・見出し等)は引き継ぎ、固有キーだけ既定値で補う。
      values: mergeDefaults(prev.values, TEMPLATES[template]),
    }));
  };

  return (
    <div className="flex h-screen w-screen overflow-hidden text-gray-900">
      {/* 左ペイン: 編集フォーム */}
      <aside className="w-96 shrink-0 overflow-y-auto border-r border-gray-200 bg-gray-50">
        <div className="flex flex-col gap-6 p-6">
          <header>
            <h1 className="text-lg font-bold">LP デザインエディタ</h1>
            <p className="mt-1 text-sm text-gray-500">
              テンプレート・色・フォント・文言を編集すると右のプレビューに即反映されます。
              プレビュー内の画像枠をクリックすると画像を差し込めます。
            </p>
          </header>

          <section className="flex flex-col gap-4">
            <h2 className="text-xs font-semibold tracking-wide text-gray-400 uppercase">
              テンプレート
            </h2>
            <SegmentToggle
              label="レイアウト"
              options={TEMPLATE_LIST}
              value={config.template}
              onChange={setTemplate}
            />
          </section>

          {/* テンプレートの fields からフォームを自動生成する。 */}
          {GROUP_ORDER.map((group) => {
            const fields = def.fields.filter((f) => f.group === group);
            if (fields.length === 0) return null;
            return (
              <section key={group} className="flex flex-col gap-4">
                <h2 className="text-xs font-semibold tracking-wide text-gray-400 uppercase">
                  {group}
                </h2>
                {fields.map((f) => (
                  <FieldControl
                    key={f.key}
                    field={f}
                    value={config.values[f.key] ?? ""}
                    onChange={(v) => setValue(f.key, v)}
                    onPick={() => openPicker(f.key)}
                  />
                ))}
              </section>
            );
          })}

          <button
            type="button"
            onClick={() => setShowOutput(true)}
            className="mt-2 rounded-lg bg-gray-900 px-4 py-2.5 font-semibold text-white transition hover:bg-gray-700"
          >
            出力
          </button>

          {showOutput && (
            <OutputPanel config={config} onClose={() => setShowOutput(false)} />
          )}
        </div>
      </aside>

      {/* 右ペイン: プレビュー */}
      <main className="min-w-0 flex-1 bg-gray-100">
        <iframe
          ref={iframeRef}
          title="preview"
          sandbox="allow-same-origin"
          srcDoc={skeleton}
          onLoad={() => setPreviewReady(true)}
          className="h-full w-full border-0"
        />
      </main>

      {/* 画像スロットのクリック / 画像フィールドから開くファイル選択(非表示)。 */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={onPickFile}
        className="hidden"
      />
    </div>
  );
}

export default App;

// --- フィールド1つを種別に応じたコントロールへ振り分ける ---

function FieldControl(props: {
  field: Field;
  value: string;
  onChange: (v: string) => void;
  onPick: () => void;
}) {
  const { field, value, onChange, onPick } = props;
  switch (field.kind) {
    case "color":
      return <ColorField label={field.label} value={value} onChange={onChange} />;
    case "font":
      return (
        <SegmentToggle<FontKind>
          label={field.label}
          options={FONT_OPTIONS}
          value={(value as FontKind) || "gothic"}
          onChange={onChange}
        />
      );
    case "text":
      return <TextField label={field.label} value={value} onChange={onChange} />;
    case "textarea":
      return (
        <TextAreaField label={field.label} value={value} onChange={onChange} />
      );
    case "image":
      return (
        <ImageField
          label={field.label}
          value={value}
          onPick={onPick}
          onClear={() => onChange("")}
        />
      );
  }
}

// --- 左ペインのローカル小コンポーネント群 ---

function ColorField(props: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <label className="flex items-center justify-between gap-3">
      <span className="text-sm font-medium">{props.label}</span>
      <span className="flex items-center gap-2">
        <span className="font-mono text-xs text-gray-500">{props.value}</span>
        <input
          type="color"
          value={props.value}
          onChange={(e) => props.onChange(e.target.value)}
          className="h-8 w-10 cursor-pointer rounded border border-gray-300 bg-white p-0.5"
        />
      </span>
    </label>
  );
}

function SegmentToggle<T extends string>(props: {
  label: string;
  options: { value: T; label: string }[];
  value: T;
  onChange: (v: T) => void;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-sm font-medium">{props.label}</span>
      <div className="inline-flex rounded-lg border border-gray-300 bg-white p-0.5">
        {props.options.map((opt) => {
          const active = opt.value === props.value;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => props.onChange(opt.value)}
              className={`flex-1 rounded-md px-3 py-1.5 text-sm transition ${
                active
                  ? "bg-gray-900 font-semibold text-white"
                  : "text-gray-600 hover:bg-gray-100"
              }`}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function TextField(props: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-sm font-medium">{props.label}</span>
      <input
        type="text"
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-gray-900 focus:outline-none"
      />
    </label>
  );
}

function TextAreaField(props: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-sm font-medium">{props.label}</span>
      <textarea
        rows={2}
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        className="resize-none rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-gray-900 focus:outline-none"
      />
    </label>
  );
}

function ImageField(props: {
  label: string;
  value: string;
  onPick: () => void;
  onClear: () => void;
}) {
  const has = props.value.length > 0;
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-sm font-medium">{props.label}</span>
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={props.onPick}
          className="flex h-14 w-20 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-dashed border-gray-300 bg-white text-lg text-gray-400 hover:border-gray-900 hover:text-gray-700"
          title="画像を選択"
        >
          {has ? (
            <img
              src={props.value}
              alt=""
              className="h-full w-full object-cover"
            />
          ) : (
            "＋"
          )}
        </button>
        <div className="flex flex-col items-start gap-1">
          <button
            type="button"
            onClick={props.onPick}
            className="text-sm text-gray-700 hover:underline"
          >
            {has ? "画像を変更" : "画像を選択"}
          </button>
          {has && (
            <button
              type="button"
              onClick={props.onClear}
              className="text-xs text-gray-400 hover:text-gray-700"
            >
              削除
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

type OutputKind = "prompt" | "yaml";

const OUTPUT_KINDS: { value: OutputKind; label: string }[] = [
  { value: "prompt", label: "プロンプト" },
  { value: "yaml", label: "YAML" },
];

const PROMPT_TARGETS: { value: PromptTarget; label: string }[] = [
  { value: "code", label: "Claude Code" },
  { value: "design", label: "Claude Design" },
];

function OutputPanel(props: { config: DesignConfig; onClose: () => void }) {
  const [kind, setKind] = useState<OutputKind>("prompt");
  const [target, setTarget] = useState<PromptTarget>("code");
  const [copied, setCopied] = useState(false);

  const body = useMemo(
    () =>
      kind === "yaml"
        ? toYaml(props.config)
        : buildPrompt(props.config, target),
    [kind, target, props.config],
  );
  const filename = kind === "yaml" ? "design.yml" : `prompt-${target}.md`;
  const mime = kind === "yaml" ? "text/yaml" : "text/markdown";

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-gray-200 bg-white p-3">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-gray-500">出力</span>
        <button
          type="button"
          onClick={props.onClose}
          className="text-xs text-gray-400 hover:text-gray-700"
        >
          閉じる
        </button>
      </div>

      <SegmentToggle<OutputKind>
        label="形式"
        options={OUTPUT_KINDS}
        value={kind}
        onChange={(v) => {
          setKind(v);
          setCopied(false);
        }}
      />

      {kind === "prompt" && (
        <SegmentToggle<PromptTarget>
          label="宛先"
          options={PROMPT_TARGETS}
          value={target}
          onChange={(v) => {
            setTarget(v);
            setCopied(false);
          }}
        />
      )}

      <pre className="max-h-72 overflow-auto rounded bg-gray-900 p-3 font-mono text-xs whitespace-pre-wrap wrap-break-word text-gray-100">
        {body}
      </pre>

      <div className="flex gap-2">
        <button
          type="button"
          onClick={async () => {
            if (await copyText(body)) {
              setCopied(true);
              setTimeout(() => setCopied(false), 1500);
            }
          }}
          className="flex-1 rounded-md border border-gray-300 px-3 py-1.5 text-sm hover:bg-gray-100"
        >
          {copied ? "コピーしました" : "コピー"}
        </button>
        <button
          type="button"
          onClick={() => downloadText(body, filename, mime)}
          className="flex-1 rounded-md border border-gray-300 px-3 py-1.5 text-sm hover:bg-gray-100"
        >
          ダウンロード
        </button>
      </div>

      <button
        type="button"
        onClick={() =>
          downloadText(
            buildPreviewHtml(props.config),
            "landing.html",
            "text/html",
          )
        }
        className="rounded-md border border-dashed border-gray-300 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100"
      >
        プレビューHTML(.html)をダウンロード
      </button>
    </div>
  );
}
